import React, { useEffect, useState } from "react";
import { fmtDateTime } from "@/lib/dates";
import { api, apiErr } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CalendarClock, Users, Video } from "lucide-react";

export default function TrainerSessions() {
  const [sessions, setSessions] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/sessions/trainer/mine").then((r) => setSessions(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAttendance = (s) => { setOpenId(s.id); setPicked((s.attendees || []).filter((a) => a.attended).map((a) => a.user_id)); };

  const saveAttendance = async (s) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/sessions/${s.id}/attendance`, { user_ids: picked });
      toast.success(`Attendance saved · ${data.certificates_issued} certificates issued`);
      setOpenId(null); load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggle = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <div>
      <PageHeader title="My assigned sessions" subtitle="Your upcoming live sessions and attendee lists. Mark attendance to issue certificates." />
      <div className="grid md:grid-cols-2 gap-5">
        {sessions.map((s) => {
          const dt = new Date(s.scheduled_at);
          return (
            <div key={s.id} data-testid={`trainer-session-${s.id}`} className="bg-cream-card border border-cream-deep rounded-2xl p-6">
              <div className="flex items-start justify-between">
                <div><Badge className="bg-brand-light text-brand hover:bg-brand-light">{s.course_type}</Badge><h3 className="font-heading font-bold text-brand text-lg mt-2">{s.title}</h3></div>
                <Video className="h-6 w-6 text-coral" />
              </div>
              <div className="text-sm text-muted-foreground mt-4 space-y-1.5">
                <div className="flex items-center gap-2"><CalendarClock className="h-4 w-4" />{fmtDateTime(dt)}</div>
                <div className="flex items-center gap-2"><Users className="h-4 w-4" />{(s.attendees || []).length} attendees</div>
              </div>
              {s.zoom_link && <a href={s.zoom_link} target="_blank" rel="noreferrer" className="text-sm text-brand underline mt-2 inline-block">Zoom link</a>}
              <Dialog open={openId === s.id} onOpenChange={(o) => (o ? openAttendance(s) : setOpenId(null))}>
                <DialogTrigger asChild><Button data-testid={`attendance-open-${s.id}`} className="mt-4 w-full rounded-full bg-brand hover:bg-brand-hover text-white">Mark attendance</Button></DialogTrigger>
                <DialogContent data-testid="attendance-dialog">
                  <DialogHeader><DialogTitle>Attendance · {s.title}</DialogTitle></DialogHeader>
                  <div className="space-y-2 max-h-72 overflow-auto">
                    {(s.attendees || []).map((a) => (
                      <label key={a.user_id} className="flex items-center gap-3 text-sm cursor-pointer p-2 rounded hover:bg-cream">
                        <Checkbox data-testid={`attendee-${a.user_id}`} checked={picked.includes(a.user_id)} onCheckedChange={() => toggle(a.user_id)} />
                        {a.name}{a.attended && <Badge variant="outline" className="ml-auto text-rag-green">Certified</Badge>}
                      </label>
                    ))}
                    {(s.attendees || []).length === 0 && <p className="text-sm text-muted-foreground">No bookings yet.</p>}
                  </div>
                  <DialogFooter><Button data-testid={`attendance-save-${s.id}`} onClick={() => saveAttendance(s)} disabled={busy} className="rounded-full bg-coral text-white">Save & issue certificates</Button></DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          );
        })}
        {sessions.length === 0 && <p className="text-muted-foreground">No sessions assigned to you yet.</p>}
      </div>
    </div>
  );
}
